import {
  EIP1193Options,
  currentProvider,
  getDownload,
  getIsInstall,
  installWalletMessage
} from '@bitget-wallet/web3-sdk'
import type {
  Actions,
  AddEthereumChainParameter,
  Provider,
  ProviderConnectInfo,
  ProviderRpcError,
  WatchAssetParameters,
} from '@web3-react/types'
import { Connector } from '@web3-react/types'
import { CoinbaseWallet } from '@web3-react/coinbase-wallet'
import { initializeConnector } from '@web3-react/core/core/core'
import { GnosisSafe } from '@web3-react/gnosis-safe'
import { MetaMask } from '@web3-react/metamask'
import { Network } from '@web3-react/network'
import { WalletConnect as WalletConnectV2 } from '@web3-react/walletconnect-v2'
import { ChainId } from '@ape.swap/sdk'
import { RPC_URLS } from 'config/constants/networks2'
import { RPC_PROVIDERS } from 'config/constants/providers'
import { MAINNET_CHAINS } from 'config/constants/chains' 
import { Connection, ConnectionType } from './types'

type BitgetWalletProvider = Provider & {
  isBitkeep?: boolean
  isConnected?: () => boolean
  providers?: BitgetWalletProvider[]
  get chainId(): string
  get accounts(): string[]
}

export class NoBitgetWalletError extends Error {
  public constructor() {
    super('BitgetWallet not installed')
    this.name = NoBitgetWalletError.name
    Object.setPrototypeOf(this, NoBitgetWalletError.prototype)
  }
}
type BitgetWalletConnectorOptions = EIP1193Options & {
  /**
   * Connector automatically connects when used as Safe App.
   *
   * This flag simulates the disconnect behavior by keeping track of connection status in storage
   * and only autoconnecting when previously connected by user action (e.g. explicitly choosing to connect).
   *
   * @default false
   */
  shimDisconnect?: boolean
}

function parseChainId(chainId: string) {
  return Number.parseInt(chainId, 16)
}
 
// Egyetlen BitKeepConstructorArgs export
export interface BitgetWalletConstructorArgs {
  actions: Actions
  options?: BitgetWalletConnectorOptions
  onError?: (error: Error) => void
}

export class BitgetWallet extends Connector {
  /** {@inheritdoc Connector.provider} */
  public provider?: BitgetWalletProvider

  private readonly options?: BitgetWalletConnectorOptions

  constructor({ actions, options, onError }: BitgetWalletConstructorArgs) {
    super(actions, onError)
    this.options = options
  }

  private async isomorphicInitialize(): Promise<void> {
    const provider = currentProvider();
    if (getIsInstall()) {
      this.provider = provider as BitgetWalletProvider
      this.provider.on('connect', ({ chainId }: ProviderConnectInfo): void => {
        this.actions.update({ chainId: parseChainId(chainId) })
      })
      this.provider.on('disconnect', (error: ProviderRpcError): void => {
        // 1013 indicates that BitgetWallet is attempting to reestablish the connection
        // https://github.com/BitgetWallet/providers/releases/tag/v8.0.0
        if (error.code === 1013) {
          console.debug('BitgetWallet logged connection error 1013: "Try again later"')
          return
        }
        this.actions.resetState()
        this.onError?.(error)
      })

      this.provider.on('chainChanged', (chainId: string): void => {
        this.actions.update({ chainId: parseChainId(chainId) })
      })

      this.provider.on('accountsChanged', (accounts: string[]): void => {
        if (accounts.length === 0) {
          // handle this edge case by disconnecting
          this.actions.resetState()
        } else {
          this.actions.update({ accounts })
        }
      })
    } else {
      window.open(getDownload(), '_blank')
      throw new Error(installWalletMessage)
    }
  }
 /** {@inheritdoc Connector.connectEagerly} */

 public async connectEagerly(): Promise<void> {

  const cancelActivation = this.actions.startActivation()

  try {
    await this.isomorphicInitialize()
    if (!this.provider) return cancelActivation()
     
    // Wallets may resolve eth_chainId and hang on eth_accounts pending user interaction, which may include changing
    // chains; they should be requested serially, with accounts first, so that the chainId can settle.
    const accounts = (await this.provider.request({ method: 'eth_accounts' })) as string[]
    if (!accounts.length) throw new Error('No accounts returned')
    const chainId = (await this.provider.request({ method: 'eth_chainId' })) as string
    this.actions.update({ chainId: parseChainId(chainId), accounts })
} catch (error) {
    console.debug('Could not connect eagerly', error);
    this.actions.resetState();
    throw error; // hiba visszadobása
  }
  // Hiba nélkül véget ért a metódus futása, ezért visszaadjuk a Promise-et
  return Promise.resolve();
}

public async activate(desiredChainIdOrChainParameters?: number | AddEthereumChainParameter): Promise<void> {
  let cancelActivation: () => void
  if (!this.provider?.isConnected?.()) cancelActivation = this.actions.startActivation()

  return this.isomorphicInitialize()
    .then(async () => {
      if (!this.provider) throw new NoBitgetWalletError()

      // Wallets may resolve eth_chainId and hang on eth_accounts pending user interaction, which may include changing
      // chains; they should be requested serially, with accounts first, so that the chainId can settle.
      const accounts = (await this.provider.request({ method: 'eth_requestAccounts' })) as string[]
      const chainId = (await this.provider.request({ method: 'eth_chainId' })) as string
      const receivedChainId = parseChainId(chainId)
      const desiredChainId =
        typeof desiredChainIdOrChainParameters === 'number'
          ? desiredChainIdOrChainParameters
          : desiredChainIdOrChainParameters?.chainId

      // if there's no desired chain, or it's equal to the received, update
      if (!desiredChainId || receivedChainId === desiredChainId)
        return this.actions.update({ chainId: receivedChainId, accounts })

      const desiredChainIdHex = `0x${desiredChainId.toString(16)}`

      // if we're here, we can try to switch networks
      return this.provider
        .request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: desiredChainIdHex }],
        })
        .catch((error: ProviderRpcError) => {
          const errorCode = (error.data as any)?.originalError?.code || error.code
          if (errorCode === 4902 && typeof desiredChainIdOrChainParameters !== 'number') {
            if (!this.provider) throw new Error('No provider')
            // if we're here, we can try to add a new network
            return this.provider.request({
              method: 'wallet_addEthereumChain',
              params: [{ ...desiredChainIdOrChainParameters, chainId: desiredChainIdHex }],
            })
          }
          throw error
        })
        .then(() => this.activate(desiredChainId))
    })
    .catch((error) => {
      cancelActivation?.()
      throw error
    })
  }

  public async watchAsset({ address, symbol, decimals, image }: WatchAssetParameters): Promise<true> {
    if (!this.provider) throw new Error('No provider')

    return this.provider
      .request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20', // Initially only supports ERC20, but eventually more!
          options: {
            address, // The address that the token is at.
            symbol, // A ticker symbol or shorthand, up to 5 chars.
            decimals, // The number of decimals in the token
            image, // A string url of the token logo
          },
        },
      })
      .then((success) => {
        if (!success) throw new Error('Rejected')
        return true
      })
  }
}
// Connections based from uniswap interface 
// https://github.com/Uniswap/interface/blob/main/src/connection/index.ts
const [mainnet, ...optionalChains] = MAINNET_CHAINS

function onError(error: Error) {
  console.debug(`web3-react error: ${error}`)
}

const [web3Network, web3NetworkHooks] = initializeConnector<Network>(
  (actions) =>
    new Network({
      actions,
      urlMap: RPC_PROVIDERS,
      defaultChainId: ChainId.BSC,
    }),
)
export const networkConnection: Connection = {
  connector: web3Network,
  hooks: web3NetworkHooks,
  type: ConnectionType.NETWORK,
}

const [web3Injected, web3InjectedHooks] = initializeConnector<MetaMask>((actions) => new MetaMask({ actions, onError }))
export const injectedConnection: Connection = {
  connector: web3Injected,
  hooks: web3InjectedHooks,
  type: ConnectionType.INJECTED,
}

const [web3GnosisSafe, web3GnosisSafeHooks] = initializeConnector<GnosisSafe>((actions) => new GnosisSafe({ actions }))
export const gnosisSafeConnection: Connection = {
  connector: web3GnosisSafe,
  hooks: web3GnosisSafeHooks,
  type: ConnectionType.GNOSIS_SAFE,
}

export const [web3WalletConnectV2, web3WalletConnectHooksV2] = initializeConnector<WalletConnectV2>(
  (actions) =>
    new WalletConnectV2({
      actions,
      options: {
        projectId:'ae3e4720b890d1efc8745f90a2237732',
        chains: [mainnet],
        optionalChains,  
        showQrModal: true,
      },  
      onError,
    }),
)

const [web3Bitget, web3BitgetHooks] = initializeConnector<BitgetWallet>(
  (actions) =>
    new BitgetWallet({
      actions,
      options: {
        // Az időtúllépés (milliszekundumban), ha a kapcsolódás túl hosszú ideig tart
      },
      onError,
    }),
);

export const walletConnectConnection: Connection = {
  connector: web3WalletConnectV2,
  hooks: web3WalletConnectHooksV2,
  type: ConnectionType.WALLET_CONNECT,
}

const [web3CoinbaseWallet, web3CoinbaseWalletHooks] = initializeConnector<CoinbaseWallet>(
  (actions) =>
    new CoinbaseWallet({
      actions,
      options: {
        url: RPC_URLS[ChainId.BSC][0],
        appName: 'ApeSwap',
        appLogoUrl: 'favicon.ico',
        reloadOnDisconnect: false,
      },
      onError,
    }),
)
export const coinbaseWalletConnection: Connection = {
  connector: web3CoinbaseWallet,
  hooks: web3CoinbaseWalletHooks,
  type: ConnectionType.COINBASE_WALLET,
}



export const bitgetConnectConnection: Connection = {
  connector: web3Bitget,
  hooks: web3BitgetHooks,
  type: ConnectionType.BitgetConnect,
};

 

// Order this list for wallet display preferences
export const listOfConnections: Connection[] = [
  injectedConnection,
  walletConnectConnection,
  coinbaseWalletConnection,
  gnosisSafeConnection,
  networkConnection,
  bitgetConnectConnection, // BitgetConnect hozzáadása a listához
 ]

export const connectionByType: Record<ConnectionType, Connection> = {
  [ConnectionType.INJECTED]: injectedConnection,
  [ConnectionType.COINBASE_WALLET]: coinbaseWalletConnection,
  [ConnectionType.WALLET_CONNECT]: walletConnectConnection,
  [ConnectionType.GNOSIS_SAFE]: gnosisSafeConnection,
  [ConnectionType.NETWORK]: networkConnection,
  [ConnectionType.BitgetConnect]: bitgetConnectConnection,
 }